/**
 * The game's sound on the web (decision 0022), a small interface of the app's own: the core and
 * the renderer know nothing of it. Web Audio, with a gain for each channel.
 *
 * A browser plays nothing before the player touches the page, so `unlock` is called from the
 * first touch, click or key; only then is anything fetched. Music streams from two audio elements
 * taking turns, so a loop of minutes is never decoded into memory: the next round starts at the
 * manifest's `nextAtMs` while the last fades out under its own fade. Effects are short: each is
 * decoded into a buffer once and played on as many voices as overlap, up to MAX_VOICES. The
 * ambience's loops are cut to loop and decode to exactly their length on every browser, so each
 * loops its buffer as it is, on a gain of its own that follows what `ambience.ts` hears; a loop
 * is fetched the first time it is wanted, so a world with no water never fetches the lake.
 */
import { type Category, SOUNDS, type SoundDef, type SoundId } from "@mistsweeper/assets";
import { Terrain } from "@mistsweeper/core";
import type { Levels } from "./ambience.ts";

/** Every sound file, by its name in the manifest; a test checks that none is missing. */
export const SOUND_URLS: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(
    import.meta.glob<string>("../../../packages/assets/sounds/*.m4a", {
      eager: true,
      query: "?url",
      import: "default",
    }),
  ).map(([path, url]) => [path.slice(path.lastIndexOf("/") + 1), url]),
);

/** How long the music takes to come and go when it is turned on or off, or steps back. */
const FADE_S = 1.2;
/** The music's level, times its own, while it steps back at the end of a run. */
const DUCKED = 0.35;
/** At most this many effects sound at once, so a busy finger cannot pile them up. */
const MAX_VOICES = 12;
/** How long an ambience's loop takes to come most of the way to a new level, in seconds. */
const AMBIENCE_EASE_S = 0.6;
/** Each effect's file keeps this much before its first sound (packages/assets/scripts/sound.ts). */
const LEAD_S = 0.005;
/** The level the files are trimmed at, in dBFS, from the same script. */
const SILENCE_DB = -50;

/** The notes a flood plays, in semitones above its file's C: the music's C minor pentatonic. */
export const FLOOD_NOTES = [0, 3, 5, 7, 10, 12] as const;
/** A sixteenth at the music's tempo, about 118 beats a minute, between a flood's notes. */
export const FLOOD_STEP_S = 60 / 118 / 4;
/**
 * The wrong banners knock a beat after the end, a sixteenth apart, at most so many, so the last
 * has knocked before the result comes.
 */
export const WRONG_START_S = 60 / 118;
export const MAX_WRONG = 6;

/**
 * How many notes a reveal of so many tiles plays: none for one, then one more each time the tiles
 * double, up to every note of FLOOD_NOTES. A flood shows what it opens, so its notes tell nothing
 * the mist hides (decision 0022).
 */
export function floodNotes(tiles: number): number {
  if (tiles < 2) return 0;
  return Math.min(FLOOD_NOTES.length, 1 + Math.floor(Math.log2(tiles)));
}

const REVEALS: Readonly<Record<Terrain, SoundId>> = {
  [Terrain.water]: "effects.reveal-water",
  [Terrain.grass]: "effects.reveal-meadow",
  [Terrain.forest]: "effects.reveal-forest",
  [Terrain.mountain]: "effects.reveal-mountain",
};

/** The sound of a tile revealed on a ground, or of a ground painted in the clearing. */
export function revealSound(terrain: Terrain): SoundId {
  return REVEALS[terrain];
}

/** The sound of a kind of creature as it rises, if it has one yet. */
export function creatureSound(kind: string): SoundId | null {
  const id = `effects.creature-${kind}`;
  return id in SOUNDS ? (id as SoundId) : null;
}

/**
 * The sound of a thing set out in the clearing, by its kind: a house, a boat or anything else of
 * wood a thud, a stone a clack, a plant a rustle, and a creature its own sound.
 */
export function placeSound(category: Category | null, family: string | null): SoundId {
  if (category === "creatures" && family)
    return creatureSound(family) ?? "effects.clearing-place-stone";
  if (category === "stones") return "effects.clearing-place-stone";
  if (category === "plants") return "effects.clearing-place-plant";
  return "effects.clearing-place-wood";
}

/** What `onset` reads of a decoded sound; an AudioBuffer is one. */
interface Samples {
  readonly length: number;
  readonly sampleRate: number;
  readonly numberOfChannels: number;
  getChannelData(channel: number): Float32Array;
}

/**
 * Where to start a decoded effect, in seconds: LEAD_S before its first sample above SILENCE_DB.
 * The files keep just that much, but a decoder that leaves AAC's priming in puts about 21 ms of
 * silence before it, which would make a tap's sound lag the tap.
 */
export function onset(samples: Samples): number {
  const threshold = 10 ** (SILENCE_DB / 20);
  let first = samples.length;
  for (let c = 0; c < samples.numberOfChannels; c++) {
    const data = samples.getChannelData(c);
    const end = Math.min(data.length, first);
    for (let i = 0; i < end; i++) {
      if (Math.abs(data[i] ?? 0) > threshold) {
        first = i;
        break;
      }
    }
  }
  if (first >= samples.length) return 0;
  return Math.max(0, first / samples.sampleRate - LEAD_S);
}

/** One variant of an effect, decoded, and where it starts. */
interface Clip {
  readonly buffer: AudioBuffer;
  readonly offset: number;
}

/** Safari's audio session (16.4 and later): ambient follows the silent switch and mixes. */
interface AudioSessionNavigator {
  audioSession?: { type: string };
}

export class GameAudio {
  private context: AudioContext | null = null;
  private musicGain: GainNode | null = null;
  private effectsGain: GainNode | null = null;
  private ambienceGain: GainNode | null = null;
  private loop: MusicLoop | null = null;
  private musicOn: boolean;
  private soundsOn: boolean;
  private ducked = false;
  private hidden = document.visibilityState === "hidden";
  private readonly clips = new Map<string, Clip[]>();
  /** The variant each effect played last, so the next is another. */
  private readonly last = new Map<string, number>();
  private voices = 0;
  private actions = 0;
  /**
   * Each ambience's loop by its sound once it plays: its gain and the level it was last sent
   * toward; and the levels last asked for.
   */
  private readonly loops = new Map<string, { gain: GainNode; level: number } | "loading">();
  private wanted: Levels = {};

  constructor(musicOn: boolean, soundsOn: boolean) {
    this.musicOn = musicOn;
    this.soundsOn = soundsOn;
    document.addEventListener("visibilitychange", () => {
      this.hidden = document.visibilityState === "hidden";
      this.apply();
    });
  }

  /**
   * Called from the player's touches and keys. The first one makes the context, starts the music
   * and fetches the effects; every one resumes a context the browser has suspended, as Safari
   * does after a call.
   */
  unlock(): void {
    try {
      if (!this.context) this.start();
      else if (this.context.state !== "running" && !this.hidden) void this.context.resume();
    } catch (error) {
      // Sound is a nicety: a browser without Web Audio plays the game in silence.
      console.error(error);
    }
  }

  setMusic(on: boolean): void {
    this.musicOn = on;
    this.apply();
  }

  /** The player's Sounds switch: every effect, and the ambience. */
  setSounds(on: boolean): void {
    this.soundsOn = on;
    this.apply();
  }

  /** The music steps back while a creature ends the run, and comes back with the next run. */
  duck(on: boolean): void {
    this.ducked = on;
    this.apply();
  }

  /**
   * Plays an effect `delay` seconds from now, `semitones` above the note it was made at, at
   * `level` times its own, one of its variants other than the last. Nothing plays before the first
   * touch, while the page is hidden, with the sounds off, or before the effect has loaded.
   */
  play(id: SoundId, delay = 0, semitones = 0, level = 1): void {
    const context = this.context;
    const channel = this.effectsGain;
    if (!context || !channel || !this.soundsOn || this.hidden || context.state !== "running") {
      return;
    }
    const clips = this.clips.get(id);
    if (!clips || clips.length === 0 || this.voices >= MAX_VOICES) return;
    let index = Math.floor(Math.random() * clips.length);
    if (clips.length > 1 && index === this.last.get(id)) index = (index + 1) % clips.length;
    this.last.set(id, index);
    const clip = clips[index];
    if (!clip) return;
    const source = context.createBufferSource();
    source.buffer = clip.buffer;
    source.playbackRate.value = 2 ** (semitones / 12);
    const gain = context.createGain();
    gain.gain.value = SOUNDS[id].gain * level;
    source.connect(gain).connect(channel);
    this.voices++;
    source.addEventListener("ended", () => {
      this.voices--;
      gain.disconnect();
    });
    source.start(context.currentTime + delay, clip.offset);
  }

  /**
   * The ambience's levels from 0 to 1 by sound, as `ambience.ts` hears the screen; a loop not
   * named is quiet. Each loop starts the first time it is wanted and eases to its level.
   */
  setAmbience(levels: Levels): void {
    this.wanted = levels;
    const context = this.context;
    const channel = this.ambienceGain;
    if (!context || !channel || context.state !== "running") return;
    for (const sound of Object.values(SOUNDS) as SoundDef[]) {
      if (sound.channel !== "ambience") continue;
      const level = Math.min(1, Math.max(0, levels[sound.id as SoundId] ?? 0));
      const loop = this.loops.get(sound.id);
      if (loop === undefined) {
        if (level > 0.001) void this.startLoop(context, channel, sound);
      } else if (loop !== "loading" && Math.abs(level - loop.level) > 0.002) {
        // Only a level that moved is sent, so a still screen adds nothing to the gain's timeline.
        loop.level = level;
        loop.gain.gain.setTargetAtTime(level * sound.gain, context.currentTime, AMBIENCE_EASE_S);
      }
    }
  }

  /**
   * Fetches and decodes a loop, and starts it, quiet, at a place of its own along itself, so loops
   * of one length do not repeat together; then it eases to the level last asked for. One that
   * fails is not tried again.
   */
  private async startLoop(
    context: AudioContext,
    channel: GainNode,
    sound: SoundDef,
  ): Promise<void> {
    this.loops.set(sound.id, "loading");
    try {
      const url = SOUND_URLS[sound.files[0] ?? ""];
      if (!url) throw new Error(`No file for ${sound.id}`);
      const buffer = await context.decodeAudioData(await (await fetch(url)).arrayBuffer());
      const gain = context.createGain();
      gain.gain.value = 0;
      gain.connect(channel);
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      source.connect(gain);
      source.start(0, Math.random() * buffer.duration);
      this.loops.set(sound.id, { gain, level: 0 });
      this.setAmbience(this.wanted);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Plays the sound of what the player did, a thing set out or the compass turning the world, in
   * place of the tap of the control that did it: a control's tap waits for its action and plays
   * only when `acted` has not moved meanwhile.
   */
  act(id: SoundId, delay = 0, semitones = 0): void {
    this.actions++;
    this.play(id, delay, semitones);
  }

  /** How many actions have sounded by themselves (`act`). */
  get acted(): number {
    return this.actions;
  }

  private start(): void {
    const session = (navigator as AudioSessionNavigator).audioSession;
    if (session) session.type = "ambient";
    const context = new AudioContext();
    this.context = context;
    const music = context.createGain();
    music.gain.value = 0;
    music.connect(context.destination);
    this.musicGain = music;
    const effects = context.createGain();
    effects.gain.value = this.soundsOn ? 1 : 0;
    effects.connect(context.destination);
    this.effectsGain = effects;
    const ambience = context.createGain();
    ambience.gain.value = this.soundsOn ? 1 : 0;
    ambience.connect(context.destination);
    this.ambienceGain = ambience;
    this.loop = new MusicLoop(context, music, SOUNDS["music.forest"]);
    this.apply();
    void this.loadEffects(context);
  }

  /** Fetches and decodes every effect, each variant on its own, so one that fails leaves the rest. */
  private async loadEffects(context: AudioContext): Promise<void> {
    const loads: Promise<void>[] = [];
    for (const sound of Object.values(SOUNDS) as SoundDef[]) {
      if (sound.channel !== "effects") continue;
      const clips: Clip[] = [];
      this.clips.set(sound.id, clips);
      for (const file of sound.files) {
        const url = SOUND_URLS[file];
        if (!url) continue;
        loads.push(
          fetch(url)
            .then((response) => response.arrayBuffer())
            .then((data) => context.decodeAudioData(data))
            .then((buffer) => {
              clips.push({ buffer, offset: onset(buffer) });
            })
            .catch((error: unknown) => console.error(error)),
        );
      }
    }
    await Promise.all(loads);
  }

  /** Sets the levels and what plays from the settings, the page's visibility and the ducking. */
  private apply(): void {
    const context = this.context;
    if (!context || !this.musicGain || !this.effectsGain || !this.ambienceGain || !this.loop) {
      return;
    }
    if (this.hidden) {
      this.loop.pauseAfter(0);
      void context.suspend();
      return;
    }
    if (context.state !== "running") void context.resume();
    const now = context.currentTime;
    const music = this.musicGain.gain;
    music.cancelScheduledValues(now);
    music.setValueAtTime(music.value, now);
    music.linearRampToValueAtTime(this.musicOn ? (this.ducked ? DUCKED : 1) : 0, now + FADE_S);
    // The Sounds switch is the effects' and the ambience's both.
    for (const channel of [this.effectsGain.gain, this.ambienceGain.gain]) {
      channel.cancelScheduledValues(now);
      channel.setValueAtTime(channel.value, now);
      channel.linearRampToValueAtTime(this.soundsOn ? 1 : 0, now + 0.05);
    }
    if (this.musicOn) this.loop.play();
    else this.loop.pauseAfter(FADE_S);
  }
}

/**
 * A loop from two audio elements taking turns, each through a gain of its own level into the
 * channel. Element A plays from the start; when it reaches `nextAtMs`, B starts from the start
 * while A plays out its fade, then they swap.
 */
class MusicLoop {
  private readonly players: readonly [HTMLAudioElement, HTMLAudioElement];
  private current: 0 | 1 = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pauseTimer: ReturnType<typeof setTimeout> | null = null;
  private playing = false;

  private readonly sound: SoundDef;

  constructor(context: AudioContext, channel: GainNode, sound: SoundDef) {
    this.sound = sound;
    const url = SOUND_URLS[sound.files[0] ?? ""];
    const player = () => {
      const element = new Audio(url);
      element.preload = "auto";
      const level = context.createGain();
      level.gain.value = sound.gain;
      context.createMediaElementSource(element).connect(level).connect(channel);
      // The timer counts from where the element was; a seek, or a stall caught up, moves it.
      element.addEventListener("seeked", () => {
        if (this.playing && element === this.players[this.current]) this.schedule();
      });
      return element;
    };
    this.players = [player(), player()];
  }

  play(): void {
    if (this.pauseTimer !== null) clearTimeout(this.pauseTimer);
    this.pauseTimer = null;
    if (this.playing) return;
    this.playing = true;
    // What was paused plays on from where it was.
    for (const element of this.players) {
      if (element.currentTime > 0 && !element.ended) void element.play().catch(() => {});
    }
    const now = this.players[this.current];
    if (now.paused) void now.play().catch(() => {});
    this.schedule();
  }

  /** Pauses once a fade out has run, or at once for 0, unless play comes first. */
  pauseAfter(seconds: number): void {
    if (!this.playing) return;
    if (seconds <= 0) {
      this.pause();
      return;
    }
    if (this.pauseTimer !== null) return;
    this.pauseTimer = setTimeout(() => this.pause(), seconds * 1000);
  }

  private pause(): void {
    if (this.pauseTimer !== null) clearTimeout(this.pauseTimer);
    this.pauseTimer = null;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.playing = false;
    for (const element of this.players) element.pause();
  }

  /** Sets a timer for the next round from where the current one is. */
  private schedule(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    const nextAt = this.sound.loop?.nextAtMs;
    if (nextAt === undefined) return;
    const element = this.players[this.current];
    const wait = Math.max(0, nextAt - element.currentTime * 1000);
    this.timer = setTimeout(() => {
      this.timer = null;
      if (!this.playing) return;
      // A timer runs late in a busy tab: wait on if the element has not got there yet.
      if (element.currentTime * 1000 < nextAt - 50 && !element.ended) {
        this.schedule();
        return;
      }
      this.current = this.current === 0 ? 1 : 0;
      const next = this.players[this.current];
      next.currentTime = 0;
      void next.play().catch(() => {});
      this.schedule();
    }, wait);
  }
}
